// Локализация интерфейса (vue-i18n 11, Composition-режим + глобальная инъекция $t в шаблоны).
//
// Как это работает:
// - Все тексты интерфейса лежат в `locales/<код>.js`; добавить язык = добавить файл + строку в SUPPORTED.
// - Выбор языка хранится в localStorage (переживает перезагрузку и обновление сборки).
// - Если игрок ещё не выбирал язык — берём язык устройства (navigator.language), иначе английский:
//   для незнакомого языка интерфейс на английском понятнее, чем на русском.
// - В шаблонах: {{ $t('ключ') }}; в JS-коде компонентов: import { t } from '@/i18n'.
// - Тексты, которые редактирует админка (сообщения игры, названия/описания турниров), локализуются
//   отдельно — на стороне сервера (см. store/modules/messages.js и messages_defaults.json).
import { createI18n } from 'vue-i18n';

import en from './locales/en';
import ru from './locales/ru';

export const SUPPORTED = [
  { code: 'ru', label: 'Русский', short: 'RU', flag: '🇷🇺' },
  { code: 'en', label: 'English', short: 'EN', flag: '🇬🇧' },
];
export const DEFAULT_LOCALE = 'en';
const LS_KEY = 'cz_lang';

export function supported(code) {
  return SUPPORTED.some((l) => l.code === code);
}

// Язык по умолчанию: сохранённый выбор → язык устройства (ru-RU → ru) → английский.
export function detectLocale() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    if (saved && supported(saved)) return saved;
  } catch (e) { /* приватный режим — localStorage недоступен, идём дальше */ }
  const nav = (typeof navigator !== 'undefined' && (navigator.languages || [navigator.language])) || [];
  for (const raw of nav) {
    const code = String(raw || '').toLowerCase().split('-')[0];
    if (supported(code)) return code;
  }
  return DEFAULT_LOCALE;
}

const i18n = createI18n({
  legacy: false,          // Composition-режим (в шаблонах $t доступен благодаря globalInjection)
  globalInjection: true,
  locale: detectLocale(),
  fallbackLocale: DEFAULT_LOCALE,  // нет ключа в языке игрока → английский, а не пустая строка
  messages: { ru, en },
  missingWarn: false,
  fallbackWarn: false,
});

export default i18n;

// Перевод из JS-кода компонентов (методы/вычисляемые): t('ключ', { count: 3 }).
export function t(key, params) {
  return i18n.global.t(key, params || {});
}

export function currentLocale() {
  return i18n.global.locale.value;
}

// Сменить язык: применяем, запоминаем и проставляем <html lang> (доступность + шрифты).
export function setLocale(code) {
  if (!supported(code)) return currentLocale();
  i18n.global.locale.value = code;
  try { localStorage.setItem(LS_KEY, code); } catch (e) { /* нет хранилища — язык живёт до перезагрузки */ }
  if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', code);
  return code;
}

// Локаль для дат/чисел (toLocaleString) — чтобы формат совпадал с языком интерфейса.
export function dateLocale() {
  return currentLocale() === 'ru' ? 'ru-RU' : 'en-GB';
}

if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', currentLocale());
